/**
 * Command Center — Calendar.
 * Every test flight from the database on one timeline, with major US
 * holidays / retail events and same-advertiser overlaps flagged.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CcStyles, ErrorBanner, errMsg } from "./components";
import { CalendarFlight, CcCalendar, CcMode, ccApi, ccBase } from "./api";
import { addDays, fmtDay, parseDay } from "./common";

const KIND: Record<CalendarFlight["kind"], { label: string; style: React.CSSProperties }> = {
  live:      { label: "Live",      style: { background: "rgba(245,158,11,.15)", border: "1px solid rgba(245,158,11,.5)", color: "var(--warning)" } },
  scheduled: { label: "Scheduled", style: { background: "rgba(59,130,246,.12)", border: "1px solid rgba(59,130,246,.6)", color: "var(--blue)" } },
  planning:  { label: "Planning",  style: { background: "rgba(139,92,246,.1)", border: "1px dashed rgba(139,92,246,.6)", color: "var(--violet)" } },
  ended:     { label: "Ended",     style: { background: "rgba(148,163,184,.1)", border: "1px solid var(--border-default)", color: "var(--text-secondary)" } },
  done:      { label: "Closed",    style: { background: "rgba(16,185,129,.1)", border: "1px solid rgba(16,185,129,.4)", color: "var(--success-card)" } },
};
const CONFLICT: React.CSSProperties = { background: "rgba(239,68,68,.12)", border: "1.5px solid var(--error)", color: "var(--error)" };

export default function CommandCenterCalendar({ mode }: { mode: CcMode }) {
  const navigate = useNavigate();
  const [cal, setCal] = useState<CcCalendar | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCal(null);
    ccApi.calendar(mode).then(setCal).catch(err => setError(`Failed to load calendar: ${errMsg(err)}`));
  }, [mode]);

  const scale = useMemo(() => {
    if (!cal) return null;
    const from = parseDay(cal.from) as Date, to = parseDay(cal.to) as Date;
    const span = Math.max(1, (to.getTime() - from.getTime()) / 86400000);
    const pct = (iso: string) => Math.min(100, Math.max(0, (((parseDay(iso) as Date).getTime() - from.getTime()) / 86400000 / span) * 100));
    const ticks: { label: string; left: number }[] = [];
    for (let d = new Date(from); d <= to; d = addDays(d, 14)) ticks.push({ label: fmtDay(d), left: pct(d.toISOString()) });
    return { pct, ticks };
  }, [cal]);

  const conflicts = cal?.flights.filter(f => f.conflict_with.length) || [];

  return (
    <div className="cc-page">
      <CcStyles />
      <div>
        <h1 className="cc-h1">Calendar</h1>
        <p className="cc-sub">Every test flight on one timeline, so overlaps and holidays are caught before a test is booked. Click a flight to open its test.</p>
      </div>

      <ErrorBanner msg={error} onClose={() => setError(null)} />

      {!cal || !scale ? (
        !error && <div className="cc-skel" style={{ height: 260 }} />
      ) : cal.flights.length === 0 ? (
        <div className="cc-card"><span className="cc-help">No tests with a start date yet. Plan a test to see it here.</span></div>
      ) : (
        <>
          <div className="cc-card" style={{ overflowX: "auto" }}>
            <div style={{ minWidth: 860, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: "0 0 210px" }} />
                <div style={{ flex: 1, position: "relative", height: 16, fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--text-tertiary)" }}>
                  {scale.ticks.map(tk => <span key={tk.label + tk.left} style={{ position: "absolute", left: `${tk.left}%`, whiteSpace: "nowrap" }}>{tk.label}</span>)}
                </div>
              </div>
              {cal.flights.map(f => {
                const left = scale.pct(f.start), right = scale.pct(f.end);
                const style = f.conflict_with.length ? CONFLICT : KIND[f.kind].style;
                return (
                  <div key={f.test_id} style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <div style={{ flex: "0 0 210px", fontSize: 13, display: "flex", flexDirection: "column" }}>
                      {f.advertiser}<span style={{ fontSize: 11.5, color: "var(--text-tertiary)" }}>{f.name}</span>
                    </div>
                    <div className="cc-lane" style={{ flex: 1 }}>
                      {cal.holidays.map(h => <span key={h.name} className="cc-hol" style={{ left: `${scale.pct(h.date)}%` }} title={`${h.name} · ${fmtDay(parseDay(h.date))}`} />)}
                      <span style={{ position: "absolute", top: -6, bottom: -6, width: 1, background: "var(--blue)", left: `${scale.pct(cal.today)}%`, opacity: 0.6 }} title="Today" />
                      <button className="cc-barx" onClick={() => navigate(`${ccBase(mode)}/tests/${f.test_id}`)}
                        title={f.conflict_with.length ? `Overlaps ${f.conflict_with.join(", ")}` : `${KIND[f.kind].label} · ${fmtDay(parseDay(f.start))} – ${fmtDay(parseDay(f.end))}`}
                        style={{ left: `${left}%`, width: `${Math.max(2, right - left)}%`, cursor: "pointer", font: "inherit", fontSize: 12, ...style }}>
                        {f.conflict_with.length ? "Overlap · " : `${KIND[f.kind].label} · `}{fmtDay(parseDay(f.start))} – {fmtDay(parseDay(f.end))}
                      </button>
                    </div>
                  </div>
                );
              })}
              <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 12, color: "var(--text-secondary)", paddingLeft: 222 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 2, height: 12, background: "var(--warning)" }} />Major holiday / retail event (hover for name)</span>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 1, height: 12, background: "var(--blue)" }} />Today</span>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, border: "1.5px solid var(--error)" }} />Overlapping tests on one advertiser</span>
              </div>
            </div>
          </div>

          <div className="cc-grid-2">
            <div className="cc-card">
              <span style={{ fontWeight: 600 }}>{conflicts.length ? `Overlaps (${conflicts.length})` : "No overlaps"}</span>
              {conflicts.length ? conflicts.map(f => (
                <div key={f.test_id} className="cc-note bad" style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                  <span>{f.advertiser} · {f.name} overlaps {f.conflict_with.join(", ")}.</span>
                  <button className="cc-btn" style={{ padding: "4px 10px" }} onClick={() => navigate(`${ccBase(mode)}/tests/${f.test_id}`)}>Open</button>
                </div>
              )) : <span className="cc-help">No advertiser has two tests in the same weeks.</span>}
            </div>
            <div className="cc-card">
              <span style={{ fontWeight: 600 }}>Holidays and retail events in view</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {cal.holidays.map(h => <span key={h.name} className="cc-chip">{fmtDay(parseDay(h.date))} · {h.name}</span>)}
              </div>
              <span className="cc-help">From the US marketing calendar. A flight that crosses one is flagged by the holiday guardrail.</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
